/* eslint-disable no-duplicate-imports */
import { useCallback } from 'react';
import type React from 'react';
import { useState, useEffect } from 'react';
import EventCalendarDay from './EventCalendarDay';
import EventCalendarEvents from './EventCalendarEvents';
import Icon from '2-core-elements/Icon/Icon';
import type { EventCalendarCardProps } from './EventCalendarCard';
import type { EventCalendarData } from 'cms/resolvers/eventCalendarResolver';

interface EventCalendarMonthProps {
    year: number;
    month: string;
    daysInMonth: number;
    currentDate: Date;
    data: EventCalendarData;
}

const EventCalendarMonth: React.FC<EventCalendarMonthProps> = ({ year, month, daysInMonth, currentDate, data }) => {
    const [isMouseDown, setIsMouseDown] = useState(false);

    useEffect(() => {
        const handleMouseDown = (): void => {
            setIsMouseDown(true);
        };
        const handleMouseUp = (): void => {
            setIsMouseDown(false);
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const handleHeaderKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            const button = document.querySelector(`.nav-month-selector[data-month="${event.currentTarget.id.split('_')[0]}"]`);
            (button as HTMLElement)?.focus();
        }
    }, []);

    const handleHeaderFocus = useCallback((event: React.FocusEvent<HTMLElement>) => {
        if (!isMouseDown) {
            event.currentTarget.querySelector('.tab-support')?.classList.add('show');
            const element = event.currentTarget;
            element.scrollIntoView({ behavior: 'smooth' });
            window.scrollBy(0, 0);
        }
    }, [isMouseDown]);

    const handleHeaderBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
        event.currentTarget.querySelector('.tab-support')?.classList.remove('show');
    }, []);

    const today = new Date();
    const currentMonthIndex = currentDate.getMonth();

    const daysArray = Array.from({ length: daysInMonth }, (_, index) => {
        return index + 1;
    })
        .filter(day => {
            const isCurrentMonth = currentMonthIndex === today.getMonth() && year === today.getFullYear();
            const isPastToday = isCurrentMonth && day < today.getDate(); // Skip previous days in the current month
            return !isPastToday;
        })
        .map(day => {
            const date = new Date(year, currentMonthIndex, day);
            const dayOfWeek = date.toLocaleString('default', { weekday: 'short' });

            const eventsForDay = data.eventCalendarCards.filter((event: EventCalendarCardProps) => {
                // event.date expected as 'YYYY-MM-DD' string or ISO timestamp
                let dateString: string;

                if (typeof event.date === 'string') {
                    dateString = event.date;
                } else {
                    dateString = new Date(event.date).toISOString()
                        .split('T')[0];
                }

                // Handle different date formats
                let eventYear: number, eventMonth: number, eventDay: number;

                if (dateString.includes('T')) {
                    // ISO datetime format: "2026-06-15T10:00:00Z"
                    const isoDate = new Date(dateString);
                    eventYear = isoDate.getFullYear();
                    eventMonth = isoDate.getMonth(); // Already zero-based
                    eventDay = isoDate.getDate();
                } else if (dateString.includes('-') && dateString.split('-').length === 3) {
                    // YYYY-MM-DD format
                    const [yyyy, mm, dd] = dateString.split('-').map(Number);
                    if (!yyyy || !mm || !dd || isNaN(yyyy) || isNaN(mm) || isNaN(dd)) {
                        return false;
                    }
                    eventYear = yyyy;
                    eventMonth = mm - 1; // Convert to zero-based
                    eventDay = dd;
                } else {
                    // Fallback: try to parse as Date
                    const fallbackDate = new Date(dateString);
                    if (isNaN(fallbackDate.getTime())) {
                        return false;
                    }
                    eventYear = fallbackDate.getFullYear();
                    eventMonth = fallbackDate.getMonth(); // Already zero-based
                    eventDay = fallbackDate.getDate();
                }

                return eventYear === year && eventMonth === currentMonthIndex && eventDay === day;
            });


            return { day, dayOfWeek, events: eventsForDay };
        });

    // Check if this month has any valid events after date filtering
    const hasValidEvents = daysArray.some(dayInfo => {
        return dayInfo && dayInfo.events.length > 0;
    });

    // Don't render the month at all if no valid events exist
    if (!hasValidEvents) {
        return null;
    }

    return (
        <div className="event-calendar-month-wrapper">
            <div id={`${month}-${year}`} className="event-calendar-month">
                <div className="monthly-header-wrapper">
                    <h2
                        tabIndex={0}
                        id={`${month}-${year}_header`}
                        role="button"
                        onKeyDown={handleHeaderKeyDown}
                        onFocus={handleHeaderFocus}
                        onBlur={handleHeaderBlur}
                        aria-label={`${month} ${year}, press to return to nav`}
                    >
                        <span className="event-calendar-month-month">{month}</span>
                        <span className="event-calendar-month-year">{year}</span>
                        <div className="tab-support">
                            <Icon icon="arrow-up" />
                            <span>Return to nav</span>
                        </div>
                    </h2>
                </div>
                <div className="monthly-event-group">
                    {daysArray.map(dayInfo => {
                        if (!dayInfo) {
                            return null;
                        }

                        // Only render days that have events
                        if (dayInfo.events.length === 0) {
                            return null;
                        }

                        return (
                            <div key={`${month}-${year}-${dayInfo.day}`} className={`daily-event-group`}>
                                <EventCalendarDay dayOfWeek={dayInfo.dayOfWeek} day={dayInfo.day} />
                                <EventCalendarEvents events={dayInfo.events} />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EventCalendarMonth;
